
import React, { useEffect, useState} from 'react';
import { useSelector, useDispatch} from 'react-redux';
import { useHistory } from 'react-router-dom';
import MUIDataTable from 'mui-datatables';
import { MuiThemeProvider, createTheme } from '@material-ui/core/styles';
import {
  getStoreCustomerGroupsApi,
  getCouponRulesApi,
  deleteCouponRuleApi
} from 'api';
import {
  Button,
  Icon,
  Snackbar,
  Card,
  Grow,
  TextField,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  Dialog,
  DialogTitle,
  Typography,
  DialogContent,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { Breadcrumb, BackdropCircular, ConfirmDialog } from 'components';
import { format, parseISO } from 'date-fns';
import { DeleteIcon } from 'components/CustomSVG';
import { setEditableRule } from 'redux/actions/coupon_rules';
import CouponCode from './CouponCode';
import EditCouponRule from './EditCouponRule';


const customTheme = createTheme({
  overrides: {
    MUIDataTable: {
      root: {
        tableLayout: 'auto',
      }
    }
  }
});

const CouponManager = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [openEditRuleDialog, setOpenEditRuleDialog] = useState(false);
  const {
    couponRulesTotalCount,
    allCouponRulesList = [],
    couponCodeData,
    editableRule,
    loadingCouponRules,
    loadingCoupon,
  } = useSelector((state) => state.coupon_rules);
  const {customerGroups = [] } = useSelector((state) => state.customers);
  const { stores = [] } = useSelector((state) => state.user.profile);

  const userStore = stores?.[0];
  const {storeId = '', quoteProStoreId = ''} = userStore;
  const websiteIds = `${quoteProStoreId},${storeId}`;

  const defaultFilter = {
    website_id: {
      field: 'website_id',
      value: websiteIds,
      conditionType: 'in'
    }
  };

  const [searchValue, setSearchValue] = useState('');
  const [searchConditions, setSearchConditions] = useState({});
  const [filterConditions, setFilterConditions] = useState(defaultFilter);
  const [sortField, setSortField] = useState('from_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [openCopyNotification, setOpenCopyNotification] = useState(false);
  const [couponIdToDelete, setCouponIdToDelete] = useState(null);

  useEffect(() => {
    if (!customerGroups.length) {
      getStoreCustomerGroupsApi({dispatch});
    }
  }, []);

  useEffect(() => {
    getCouponRulesApi({
      dispatch,
      sortDirection,
      sortField,
      pageSize: rowsPerPage,
      currentPage,
      filterConditions: Object.values(filterConditions)
        .filter((condition) => condition.value !== ''),
      searchConditions
    });
  }, [
    sortDirection,
    sortField,
    rowsPerPage,
    currentPage,
    filterConditions,
    searchConditions
  ]);


  const openEditRule = (ruleId) => {
    const editableRule = allCouponRulesList.find((rule) => rule.rule_id === ruleId);
    dispatch(setEditableRule(editableRule));
    setOpenEditRuleDialog(true);
  };

  const handleConfirmDelete = () => {
    deleteCouponRuleApi(dispatch, couponIdToDelete);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setOpenCopyNotification(true);
  };

  const onSearch = (searchValue) => {
    const condition = {
      field: 'name',
      value: searchValue.length ? `%${searchValue}%` : '',
      conditionType: 'like'
    };
    setSearchConditions(searchValue.length ? condition : {});
    setCurrentPage(1);
  };

  const onColumnSortChange = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };


  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <div>
        <Breadcrumb
          routeSegments={[
            { name: 'Coupon Manager' }
          ]}
        />
      </div>
      <div className="flex justify-end mr-10 mb-3">
        <Button
          variant="contained"
          color="primary"
          onClick={() => history.push({
            pathname: '/coupon-manager/create-rule'
          })}
        >
          <Icon className="mr-2">add</Icon>
          Create New Coupon Rule
        </Button>
      </div>
      <div className="mx-10 mb-10">
        <Card>
          <>
            <div className="mr-5 mt-8 flex">
              <b className="ml-5">All coupons ({couponRulesTotalCount})</b>
            </div>
            <MuiThemeProvider theme={customTheme}>
              <MUIDataTable
                data={allCouponRulesList}
                columns={[
                  {
                    name: 'name',
                    label: 'Name',
                    options: {
                      filter: false,
                      setCellHeaderProps: () => ({ style: { width: '20%'}}),
                      setCellProps: () => ({ style: { width: '20%' }})
                    }
                  },
                  {
                    name: 'description',
                    label: 'Description',
                    options: {
                      filter: false,
                      setCellHeaderProps: () => ({ style: { width: '20%'}}),
                      setCellProps: () => ({ style: { width: '20%' }}),
                    }
                  },
                  {
                    name: '',
                    label: 'Coupon Code',
                    options: {
                      filter: false,
                      setCellHeaderProps: () => ({ style: { width: '15%'}}),
                      setCellProps: () => ({ style: { width: '15%' }}),
                      customBodyRenderLite: (dataIndex) => {
                        const rule = allCouponRulesList[dataIndex];
                        const { coupon_type, rule_id } = rule;
                        const couponCodes = couponCodeData[rule_id]?.map((v) => v.code);

                        return (
                          <CouponCode
                            coupon_type={coupon_type}
                            couponCodes={couponCodes}
                            copyCode={copyCode}
                            rule_id={rule_id}
                          />
                        );
                      }
                    }
                  },
                  {
                    name: 'created_by',
                    label: 'Created By',
                    options: {
                      filter: false,
                      setCellHeaderProps: () => ({ style: { width: '10%'}}),
                      setCellProps: () => ({ style: { width: '10%' }}),
                      customBodyRenderLite: (dataIndex) => {
                        const rule = allCouponRulesList[dataIndex];
                        const createdBy = rule.extension_attributes?.created_by;

                        return createdBy || 'DermPRO staff';
                      }
                    }
                  },
                  {
                    name: 'is_active',
                    label: 'Status',
                    options: {
                      filterType: 'custom',
                      setCellHeaderProps: () => ({ style: { width: '10%' } }),
                      setCellProps: () => ({ style: { width: '10%' } }),
                      customBodyRenderLite: (dataIndex) => {
                        const rule = allCouponRulesList[dataIndex];
                        return rule.is_active ? 'Active' : 'Non Active';
                      },
                      filter: true,
                      display: true,
                      customFilterListOptions: {
                        render: (v) => {
                          if (v[0] === '') {
                            v = [];
                          }

                          return `${v[0]}`;
                        },
                        update: (filterList, _filterPos, index) => {
                          filterList[index] = [];
                          const condition = {
                            field: 'is_active',
                            value: '',
                            conditionType: 'eq'
                          };
                          setFilterConditions({ ...filterConditions, is_active: condition });
                          return filterList;
                        }
                      },
                      filterOptions: {
                        fullWidth: true,
                        display: (filterList, onChange, index, column) => {
                          return (
                            <FormControl>
                              <Select
                                displayEmpty
                                fullWidth
                                label="Status"
                                value={filterList[index] || ''}
                                onChange={(event) => {
                                  const selectedValue = event.target.value;
                                  let valueToSend;

                                  switch (selectedValue) {
                                    case '':
                                      valueToSend = '';
                                      break;
                                    case 'Active':
                                      valueToSend = true;
                                      break;
                                    case 'NonActive':
                                      valueToSend = false;
                                      break;
                                    default:
                                      break;
                                  }

                                  const condition = {
                                    field: 'is_active',
                                    value: valueToSend,
                                    conditionType: 'eq'
                                  };

                                  setFilterConditions({ ...filterConditions, is_active: condition });
                                  filterList[index] = [event.target.value];
                                  onChange(filterList[index], index, column);
                                }}
                              >
                                <MenuItem disabled value=""><em>Status</em></MenuItem>
                                <MenuItem value="Active">Active</MenuItem>
                                <MenuItem value="NonActive">Non Active</MenuItem>
                              </Select>
                            </FormControl>
                          );
                        },
                      },
                    },
                  },
                  {
                    name: 'from_date',
                    label: 'Start Date',
                    options: {
                      filter: false,
                      setCellHeaderProps: () => ({ style: { width: '15%'}}),
                      setCellProps: () => ({ style: { width: '15%' }}),
                      customBodyRenderLite: (dataIndex) => {
                        const fromDate = allCouponRulesList[dataIndex]?.from_date || '';
                        const formattedFromDate = fromDate && format(parseISO(fromDate), 'MM/dd/yyyy');

                        return formattedFromDate;
                      }
                    }
                  },
                  {
                    name: 'to_date',
                    label: 'End Date',
                    options: {
                      filter: false,
                      setCellHeaderProps: () => ({ style: { width: '15%'}}),
                      setCellProps: () => ({ style: { width: '15%' }}),
                      customBodyRenderLite: (dataIndex) => {
                        const toDate = allCouponRulesList[dataIndex]?.to_date || '';
                        const formattedToDate = toDate && format(parseISO(toDate), 'MM/dd/yyyy');

                        return formattedToDate;
                      }
                    }
                  },
                  {
                    name: '',
                    label: 'Actions',
                    options: {
                      filter: false,
                      customBodyRenderLite: (dataIndex) => {
                        const rule = allCouponRulesList[dataIndex];
                        return (
                          <div className="flex items-center">
                            <IconButton
                              title="Click here to delete coupon"
                              onClick={() => {
                                setCouponIdToDelete(rule.rule_id);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                            <IconButton
                              title="Click here to edit coupon"
                              onClick={() => {
                                openEditRule(rule.rule_id);
                              }}
                            >
                              <Icon>edit</Icon>
                            </IconButton>
                          </div>
                        );
                      }
                    }
                  }
                ]}
                options={{
                  responsive: 'standard',
                  selectableRows: 'none',
                  sort: true,
                  search: true,
                  customSearchRender: (
                    searchText,
                    handleSearch,
                    hideSearch
                  ) => {
                    return (
                      <Grow appear in timeout={300}>
                        <TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={searchValue}
                          onChange={({ target: { value } }) => {
                            setSearchValue(value);
                          }}
                          onKeyPress={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              onSearch(searchValue);
                            }
                          }}
                          placeholder="Enter rule name"
                          InputProps={{
                            endAdornment: (
                              <IconButton
                                onClick={() => {
                                  setSearchValue('');
                                  onSearch('');
                                  hideSearch();
                                }}
                              >
                                <Icon fontSize="small">
                                  clear
                                </Icon>
                              </IconButton>
                            ),
                          }}
                        />
                      </Grow>
                    );
                  },
                  filter: true,
                  filterType: 'select',
                  download: false,
                  print: false,
                  pagination: true,
                  viewColumns: true,
                  elevation: 0,
                  serverSide: true,
                  count: couponRulesTotalCount,
                  page: currentPage - 1,
                  rowsPerPage,
                  rowsPerPageOptions: [20, 40, 80, 100],
                  onFilterChange: (column, filterList, type) => {
                    if (['chip', 'reset'].includes(type)) {
                      setFilterConditions(defaultFilter);
                    }
                  },
                  onColumnSortChange: (changedColumn, direction) => onColumnSortChange(changedColumn, direction),
                  onChangePage: (currentPage) => setCurrentPage(currentPage + 1),
                  onChangeRowsPerPage: (newLimit) => setRowsPerPage(newLimit),
                }}
              />
            </MuiThemeProvider>
            {!!Object.keys(editableRule).length &&
            <Dialog
              open={openEditRuleDialog}
              onClose={() => setOpenEditRuleDialog(false)}
              maxWidth={'lg'}
              fullWidth
            >
              <DialogTitle>
                <Typography variant="h4" className="mt-4">Edit Coupon Rule</Typography>
              </DialogTitle>
              <DialogContent  className="m-4">
                <EditCouponRule
                  setOpenEditRuleDialog={setOpenEditRuleDialog}
                />
              </DialogContent>
            </Dialog>
            }
            <Snackbar
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              autoHideDuration={1000}
              onClose={() => setOpenCopyNotification(false)}
              open={openCopyNotification}
            >
              <MuiAlert elevation={6} variant="filled"  severity="info">
                Copied to clipboard
              </MuiAlert>
            </Snackbar>
          </>
        </Card>
      </div>
      <ConfirmDialog
        title="Delete coupon?"
        open={Boolean(couponIdToDelete)}
        setOpen={setCouponIdToDelete}
        onConfirm={handleConfirmDelete}
      >
        Are you sure you want to delete the coupon?
      </ConfirmDialog>
      <BackdropCircular loading={loadingCouponRules || loadingCoupon} />
    </div>
  );
};

export default CouponManager;
